import React, { useState, useEffect } from 'react'
import { api, formatDate } from '../../utils/api'
import { configDiff } from '../../utils/catalogReview'
import '../../styles/pages.css'

/**
 * Engine settings: the bounded graph.engine_config row. The adjudication
 * toggle is the kill switch — flipping it off stops LLM adjudication calls
 * on the next runner invocation (the runner itself stays manual by design).
 */
function CatalogSettings() {
  const [saved, setSaved] = useState(null)
  const [form, setForm] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [savedFlash, setSavedFlash] = useState(false)

  useEffect(() => {
    loadConfig()
  }, [])

  async function loadConfig() {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get('/api/graph/review/config')
      setSaved(response)
      setForm(response)
    } catch (err) {
      console.error('Failed to load engine config:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  function setField(field, value) {
    setForm(current => ({ ...current, [field]: value }))
    setSavedFlash(false)
  }

  const diff = saved && form ? configDiff(saved, form) : {}
  const dirty = Object.keys(diff).length > 0

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const response = await api.patch('/api/graph/review/config', diff)
      setSaved(response)
      setForm(response)
      setSavedFlash(true)
    } catch (err) {
      console.error('Failed to save engine config:', err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading || !form) {
    return (
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
          <div className="loading-spinner" />
        </div>
      </div>
    )
  }

  return (
    <>
      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
          <button
            type="button"
            className="btn btn-sm"
            style={{ marginLeft: 'var(--space-3)' }}
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div className="card" style={{ flex: '1 1 340px', maxWidth: 520, background: 'var(--color-bg-secondary)', order: 2 }}>
        <h3 style={{ marginTop: 0 }}>How the engine uses these settings</h3>
        <p className="text-sm" style={{ marginBottom: 'var(--space-3)' }}>
          When a submission item can't be matched to the catalog by exact alias,
          the engine builds a <strong>shortlist</strong> of candidate products by
          text similarity. The <strong>shortlist limit</strong> caps how many
          candidates are considered, and <strong>min similarity</strong> is the
          floor a candidate must clear to be considered at all — if nothing
          clears it, the engine refuses to guess and the item stays unresolved
          for this review queue.
        </p>
        <p className="text-sm" style={{ marginBottom: 0 }}>
          <strong>Adjudication</strong> is the LLM step that picks between
          shortlist candidates when rules can't decide. The toggle is its kill
          switch, the <strong>model</strong> is which Claude does the picking,
          and the <strong>daily budget</strong> caps LLM calls per day — past
          it, adjudication stops until tomorrow. While the engine is early,
          adjudication only runs when an operator starts it manually; there is
          no scheduled job.
        </p>
      </div>

      <div className="card" style={{ flex: '1 1 480px', maxWidth: 640, order: 1 }}>
        <h3 style={{ marginTop: 0 }}>Adjudication</h3>

        <div className="filter-group" style={{ marginBottom: 'var(--space-4)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={Boolean(form.adjudication_enabled)}
              onChange={e => setField('adjudication_enabled', e.target.checked)}
            />
            <span style={{ fontWeight: 500 }}>Adjudication enabled</span>
          </label>
          <div className="text-sm text-muted" style={{ marginTop: 4 }}>
            Kill switch for LLM adjudication of unresolved observations.
            The runner is manual — this gates whether it may call the model at all.
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <div className="filter-group">
            <label className="filter-label">Daily budget (calls)</label>
            <input
              className="form-input"
              type="number"
              min="0"
              value={form.adjudication_daily_budget}
              onChange={e => setField('adjudication_daily_budget', e.target.value)}
            />
          </div>
        </div>

        <h3>Identification shortlist</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <div className="filter-group">
            <label className="filter-label">Shortlist limit</label>
            <input
              className="form-input"
              type="number"
              min="1"
              value={form.shortlist_limit}
              onChange={e => setField('shortlist_limit', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label className="filter-label">Min similarity (0–1)</label>
            <input
              className="form-input"
              type="number"
              step="0.05"
              min="0"
              max="1"
              value={form.shortlist_min_similarity}
              onChange={e => setField('shortlist_min_similarity', e.target.value)}
            />
          </div>
        </div>

        <h3>Retailer pipeline</h3>
        <div className="filter-group" style={{ marginBottom: 'var(--space-4)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={Boolean(form.retailer_pipeline_enabled)}
              onChange={e => setField('retailer_pipeline_enabled', e.target.checked)}
            />
            <span style={{ fontWeight: 500 }}>Retailer pipeline enabled</span>
          </label>
          <div className="text-sm text-muted" style={{ marginTop: 4 }}>
            When off, the hourly block runs dry: it logs what it would do but writes
            nothing and spends nothing. Turn it on after a clean dry run.
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)', marginBottom: 'var(--space-2)' }}>
          <div className="filter-group">
            <label className="filter-label">Recategorise daily cap</label>
            <input
              className="form-input"
              type="number"
              min="0"
              value={form.recategorise_daily_cap}
              onChange={e => setField('recategorise_daily_cap', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label className="filter-label">Recategorise run slice</label>
            <input
              className="form-input"
              type="number"
              min="0"
              value={form.recategorise_run_slice}
              onChange={e => setField('recategorise_run_slice', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label className="filter-label">Mint daily cap</label>
            <input
              className="form-input"
              type="number"
              min="0"
              value={form.mint_daily_cap}
              onChange={e => setField('mint_daily_cap', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label className="filter-label">Mint run slice</label>
            <input
              className="form-input"
              type="number"
              min="0"
              value={form.mint_run_slice}
              onChange={e => setField('mint_run_slice', e.target.value)}
            />
          </div>
        </div>
        <div className="text-sm text-muted" style={{ marginBottom: 'var(--space-4)' }}>
          Daily cap is the most LLM calls a step makes per day; run slice caps one
          run. Set either to 0 to turn that step off.
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          paddingTop: 'var(--space-3)',
          borderTop: '1px solid var(--color-border)'
        }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!dirty || saving}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          {savedFlash && !dirty && (
            <span className="badge badge-success">Saved</span>
          )}
          <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>
            Last updated {formatDate(saved.updated_at)}
          </span>
        </div>
      </div>
      </div>
    </>
  )
}

export default CatalogSettings
